<template>
  <v-container fluid>
    <v-layout>
      <v-flex xs8 style="padding-bottom: 16px">
        <AppForm label="キャンペーン名">
          <v-text-field
            ref="campaignNameInput"
            v-model="campaign.name"
            hide-details
          />
          <div style="padding-top: 16px; display: flex; align-items: center;">
            <v-icon @click="openHelpPage()" color="primary"
              >mdi-help-circle</v-icon
            >
            <span
              @click="openHelpPage()"
              style="font-size: small; margin-left: 8px; cursor: pointer; color: #007bff; text-decoration:underline;"
            >
              シミュレーションを利用した入稿 ヘルプはこちら
            </span>
          </div>
        </AppForm>
      </v-flex>
      <v-flex v-if="isShowSimulationImportButton" xs6 justify-content-end>
        <v-btn @click="showSimulationImportModal">
          シミュレーションを読み込む
        </v-btn>
      </v-flex>
    </v-layout>
    <AppForm label="メニュー選択" class="app-form" />
    <v-layout v-if="!campaign.isNew">
      <div class="menu-setting-container">
        <div>
          クリエイティブ種別：{{
            CreativeTypeItems[campaign.menu.creativeType].text
          }}
        </div>
        <div>ターゲティング種別：{{ MenuTypes[campaign.menu.menuType] }}</div>
      </div>
    </v-layout>
    <v-layout v-if="campaign.isNew" class="creative-container" column>
      <p>クリエイティブ種別を指定してください</p>
      <v-radio-group
        ref="creativeType"
        v-on:click="clickCreative(creativeType)"
        v-model="creativeType"
        class="creative-types"
        row
      >
        <v-layout wrap>
          <v-flex
            v-for="(item, index) in creativeTypes"
            :key="index"
            style="width: 200px;"
          >
            <v-tooltip right>
              <template v-slot:activator="{ on }">
                <img
                  v-on="on"
                  @click="creativeIconClick(item)"
                  :src="item.iconImage"
                  :class="{
                    'creative-icon': true,
                    'creative-choice': item.value === creativeType,
                  }"
                  alt=""
                />
              </template>
              <div class="creative-icon-tooltip">
                <img :src="item.sampleImage" alt="" />
              </div>
              <div class="creative-sample-message">
                <h3 v-html="item.text" />
                <span v-html="item.sampleMessage" />
              </div>
            </v-tooltip>
            <v-radio
              :label="item.text"
              :value="item.value"
              class="creative-radio"
            />
          </v-flex>
        </v-layout>
      </v-radio-group>
      <div class="mt-2">
        <small class="red--text block">
          ※ クリエイティブ種別は登録後編集ができません。
        </small>
        <small class="red--text block">
          ※ 動画の場合、クリエイティブはメールでの入稿になります。
          入稿については、担当者へお問い合わせください。
        </small>
      </div>
    </v-layout>
    <div v-if="creativeType && campaign.isNew">
      <div>ターゲティング種別を指定してください</div>
      <v-radio-group ref="menuType" v-model="menuType">
        <div
          v-for="item in menuTypes"
          :key="item.value"
          xs4
          class="menu-type-container"
        >
          <v-radio :key="item.value" :value="item.value" />
          <div class="app-menu-icon-container">
            <AppMenuIcon :menu-type="item.value" />
            <div class="app-menu-text">
              {{ item.text }}
            </div>
          </div>
        </div>
      </v-radio-group>
    </div>

    <div v-if="filteredMenuList !== null">
      <MenuSection
        v-model="menuId"
        :menu-list="filteredMenuList"
        :edit-mode="editMode"
        :loading="loadingMenuList"
        :click-menu-func="changeSelectMenu"
      />
    </div>

    <CampaignModalSimulationImport
      @submit="importSimulation"
      :form="form"
      :system-owner="systemOwner"
      :advertiser="advertiser"
      :insertion-order="insertionOrder"
      v-model="isShowSimulationImportModal"
    />
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import uniqBy from 'lodash/uniqBy'
import Defaults from '@/models/CampaignOperation/Defaults'
import { CreativeTypes } from '@/models/Creative'
import bannerIcon from '@/assets/images/creative/banner-icon.png'
import inFeedIcon from '@/assets/images/creative/in-feed-icon.png'
import videoIcon from '@/assets/images/creative/video-icon.png'
import bannerSample from '@/assets/images/creative/banner-sample.png'
import inFeedSample from '@/assets/images/creative/in-feed-sample.png'
import videoSample from '@/assets/images/creative/video-sample.png'
import MenuSection from '@/components/Shared/Common/FormMenu'
import CampaignModalSimulationImport from '@/components/Advertiser/Operation/IO/CampaignModalSimulationImport'
import { OptimizationRepository } from '@/models/Optimization'
import { AdvancedSettingFormModel } from '@/models/CampaignOperation/Form/AdvancedSettingForm'
import {
  MenuTypes as MenuTypeConstants,
  RailwayTargetTypes,
} from '@/models/Menu/Constants'
import { CreativeAttachTargets } from '@/models/CampaignOperation'

const CreativeTypeItems = {
  'in feed': {
    value: CreativeTypes.INFEED,
    text: 'インフィード',
    iconImage: inFeedIcon,
    sampleImage: inFeedSample,
    sampleMessage:
      'アプリのコンテンツと<br/>コンテンツの間に<br/>出現する記事風の広告',
  },
  banner: {
    value: CreativeTypes.BANNER,
    text: 'バナー（静止画）',
    iconImage: bannerIcon,
    sampleImage: bannerSample,
    sampleMessage: '通常の静止画',
  },
  video: {
    value: CreativeTypes.VIDEO,
    text: '動画',
    iconImage: videoIcon,
    sampleImage: videoSample,
    sampleMessage:
      'アプリの遷移画面や<br/>動画コンテンツのスタート前に<br/>表示する動画形式広告',
  },
}
const MenuTypes = {
  [MenuTypeConstants.Geo]: 'ジオターゲティング',
  [MenuTypeConstants.Newspaper]: '新聞購読率ターゲティング',
  [MenuTypeConstants.Pta]: 'PTAターゲティング',
  [MenuTypeConstants.Automobile]: '自動車ターゲティング',
  [MenuTypeConstants.Mansion]: 'マンションターゲティング',
  [MenuTypeConstants.GeoGenome]: 'GeoGenomeターゲティング',
  [MenuTypeConstants.Railway]: '鉄道ターゲティング',
  [MenuTypeConstants.Retargeting]: 'リターゲティング',
  [MenuTypeConstants.CustomGeo]: 'カスタムジオターゲティング',
  [MenuTypeConstants.Office]: 'オフィスターゲティング',
  [MenuTypeConstants.Custom]: 'カスタムターゲティング',
}

const hasTargetStep = () => {
  if (this.form.campaign.hasGeoMenuSetting) return true

  if (this.form.campaign.hasCustomGeoMenuSetting) return true

  if (
    this.form.campaign.hasRailwayMenuSetting &&
    this.menuSetting.orderDescription.targetTypes.includes(
      RailwayTargetTypes.Station,
    )
  ) {
    return true
  }

  return false
}

export default {
  name: 'CampaignModalPageMenu',
  components: { MenuSection, CampaignModalSimulationImport },
  props: {
    form: {
      type: Object,
      required: true,
    },
    insertionOrder: {
      type: Object,
      required: true,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    systemOwner: {
      type: Object,
      required: true,
    },
    advertiser: {
      type: Object,
      required: true,
    },
    maxStep: {
      type: Number,
      required: true,
    },
    menuStep: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loadingMenuList: true,
      radioCreativeTypes: [],
      initialMenu: this.form.campaign.menu,
      menuType: undefined,
      isShowSimulationImportModal: false,
      CreativeTypeItems,
      MenuTypes,
      orderOfCreativeTypes: ['in feed', 'banner', 'video'],
    }
  },
  computed: {
    ...mapGetters('account', ['isValidateSimulation']),
    ...mapGetters('account', ['creativeVisible']),
    menuList() {
      return this.form.menus.map(menu => ({
        ...menu,
        creativeType: menu.creativeType,
      }))
    },
    campaign() {
      return this.form.campaign
    },
    menuId: {
      get() {
        return this.campaign.menuId
      },
      set(menuId) {
        const currentMenuType = (() => {
          if (this.campaign.menu === undefined) return undefined
          return this.campaign.menu.menuType
        })()
        this.form.selectMenu(menuId)
        const nextMenuType = (() => {
          if (this.campaign.menu === undefined) return undefined
          return this.campaign.menu.menuType
        })()
        if (currentMenuType === undefined || currentMenuType !== nextMenuType) {
          this.form.setMenuSetting()
        }
        this.campaign.setAdvancedSetting()

        if (this.creativeType === undefined) {
          this.creativeType = this.campaign.menu.creativeType
          this.menuType = this.campaign.menu.menuType
        }
      },
    },
    errorMessage() {
      let result = ''
      const { menuId, name, creativeType } = this.campaign
      if (name == null || name === '') {
        result = 'キャンペーン名を入力してください。'
      } else if (menuId == null) {
        result = '配信メニューを選択してください。'
      } else if (creativeType == null) {
        result = 'クリエイティブ種別を選択してください。'
      }
      return result
    },
    creativeTypeText() {
      return this.CreativeTypeItems.find(
        type => type.value === this.campaign.creativeType,
      ).text
    },
    isGeoMenuSetting() {
      return this.form.campaign.hasGeoMenuSetting
    },
    creativeTypes() {
      const creativeTypes = uniqBy(this.menuList, menu => menu.creativeType)
        .map(menu => CreativeTypeItems[menu.creativeType])
        .sort(
          (a, b) =>
            this.orderOfCreativeTypes.indexOf(a.value) -
            this.orderOfCreativeTypes.indexOf(b.value),
        )
      return creativeTypes
    },
    creativeType: {
      get() {
        return this.campaign.creativeType
      },
      set(creativeType) {
        this.campaign.changeCreativeType(creativeType)
        if (this.menuType !== undefined) return

        if (
          this.menuTypes.find(
            menuType => menuType.value === MenuTypeConstants.Geo,
          )
        ) {
          this.menuType = MenuTypeConstants.Geo
        }
      },
    },
    menuTypes() {
      return uniqBy(
        this.menuList.filter(menu => menu.creativeType === this.creativeType),
        menu => menu.menuType,
      ).map(menu => ({
        text: MenuTypes[menu.menuType],
        value: menu.menuType,
      }))
    },
    isSystemOwner() {
      return this.$store.getters['account/isSystemOwner']
    },
    isSuperAdmin() {
      return this.$store.getters['account/isSuperAdmin']
    },
    filteredMenuList() {
      if (!this.creativeType || !this.menuType) return null

      return this.menuList.filter(
        menu =>
          menu.creativeType === this.creativeType &&
          menu.menuType === this.menuType,
      )
    },
    isShowSimulationImportButton() {
      return this.menuType ? this.menuType === MenuTypeConstants.Geo : true
    },
  },
  watch: {
    async menuId() {
      if (
        this.form.campaign.menu.unitType !== 'cpc' &&
        this.form.campaign.menu.unitType !== 'cpcv'
      ) {
        const optimizationGoalObject = await this.getOptimizationGoal()
        this.campaign.goalValue = optimizationGoalObject.goalValue
      }
      if (this.form.campaign.menu.unitType === 'cpcv') {
        this.form.campaign.updateEnableOptimization(false)
      }
      if (this.form.campaign.menu.unitType === 'cpc') {
        this.form.campaign.goalValue = this.form.campaign.menu.grossPrice
      }
    },
    menuType() {
      if (this.menuType === undefined) {
        this.menuType = MenuTypeConstants.Geo
      }
    },
    filteredMenuList() {
      if (this.filteredMenuList.length === 1) {
        this.menuId = this.filteredMenuList[0].id
      }
    },
    menuList(newMenus) {
      if (newMenus.length === 0) {
        this.$confirm('', {
          okText: '閉じる',
          onlyOk: true,
          body:
            '使用できるメニューがないためキャンペーンを作成できません。\n詳しくは、システム管理者にお問い合わせください。',
        })
      }
    },
  },
  async created() {
    await this.fetchMenuList()
    const menu = this.form.campaign.menu

    if (menu !== undefined) {
      this.creativeType = menu.creativeType
      this.menuType = menu.menuType
    }

    this.$refs.campaignNameInput.focus()
  },
  methods: {
    async fetchMenuList() {
      try {
        this.loadingMenuList = true
        await this.form.fetchMenus()
      } finally {
        this.loadingMenuList = false
      }
    },
    async getOptimizationGoal() {
      const insertionOrderId = this.form.insertionOrderId
      const menuId = this.campaign.menuId
      const goalType = 'cpc'
      const optimizationGoal = await OptimizationRepository.getOptimizationGoal(
        insertionOrderId,
        menuId,
        goalType,
      )
      return optimizationGoal
    },

    async clickCreative(creativeType) {
      const warningVideoCreative = () =>
        this.$confirm('', {
          okText: '閉じる',
          onlyOk: true,
          body: `動画クリエイティブは、メールでの入稿となります。\n入稿については、担当者へお問い合わせください。`,
        })

      switch (creativeType) {
        case CreativeTypes.VIDEO:
          await warningVideoCreative()
      }
    },

    async creativeIconClick(item) {
      if (item.disable) {
        return
      }

      this.creativeType = item.value
      this.menuType = undefined
    },
    showSimulationImportModal() {
      this.$sendEventToGA('buttonClick', {
        buttonName: 'campaignModalPageMenu.simulationImportButton',
      })
      this.isShowSimulationImportModal = true
    },
    importSimulation(hashId) {
      this.$emit('importSimulation', hashId)
    },
    async changeSelectMenu(menuId) {
      if (!menuId) return false
      if (!(await this.alertResetMenuSetting())) {
        return false
      }
      const selectMenu = this.menuList.find(menu => menu.id === menuId)
      if (!(await this.alertResetCreativeSetting(selectMenu.menuType))) {
        return false
      }

      // 高度な設定のリセット
      this.campaign.useAdvancedSetting = Defaults.Campaign.useAdvancedSetting
      this.campaign.advancedSetting = new AdvancedSettingFormModel({
        menuType: selectMenu.menuType,
      })

      // クリエイティブ設定のリセット
      this.campaign.creativeAttachTarget = CreativeAttachTargets.CAMPAIGN

      this.$emit('resetMaxStep')

      return true
    },
    async alertResetMenuSetting() {
      if (this.maxStep < 3) {
        return true
      }
      return await this.$confirm(
        this.menuStep.title +
          (hasTargetStep ? ', 配信対象設定, ' : ', ') +
          '高度な設定がリセットされますがよろしいですか？',
      )
    },
    async alertResetCreativeSetting(menuType) {
      if (
        this.form.campaign.hasGeoMenuSetting &&
        menuType !== MenuTypes.Geo &&
        this.form.campaign.creativeAttachTarget ===
          CreativeAttachTargets.DELIVERY_GROUP
      ) {
        return await this.$confirm(
          'クリエイティブ設定がリセットされますがよろしいでしょうか？',
        )
      }
      return true
    },
    openHelpPage() {
      window.open(
        'https://go.geologic.co.jp/adminmanual_campaign_fromsimulation',
        '_blank',
        'noreferrer',
      )
    },
  },
}
</script>

<style scoped lang="scss">
// .app-form {
//   margin-top: 30px;
// }
.creative-types {
  ::v-deep .v-input--radio-group__input {
    align-items: center;
  }
  width: 80%;
}
.creative-icon {
  padding: 5px;
  width: 110px;
}
.icon-opacity {
  opacity: 0.3;
}
.creative-choice {
  background-color: #c8dbff;
}
.creative-radio {
  margin-top: 12px;
}
.creative-icon-tooltip {
  width: 180px;
}
.creative-sample-message {
  text-align: center;
  width: 180px;
}
.block {
  display: block;
}
.load-simulation {
  ::v-deep .v-btn {
    margin: 0;
  }
}
.creative-container {
  // padding-top: 5px;
  padding-bottom: 10px;
}
.menu-type-container {
  margin-top: 10px;
  margin-right: 20px;
  display: flex;
  flex-direction: row;
  align-items: center;
}
.app-menu-icon-container {
  display: flex;
  flex-direction: row;
  align-content: center;
  margin-left: -20px;
  margin-bottom: 10px;
}
.app-menu-text {
  margin-left: 5px;
}
.menu-setting-container {
  padding-top: 10px;
  padding-bottom: 10px;
}
.video-waring-dialog {
  text-align: center;
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 0.02em;
  font-family: 'Roboto', sans-serif;
}
</style>
